// src/components/intake/QuestionItem.jsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "../ui";
import { saveIntakePage } from "../../shared/_services/intake.services";
import { saveFailure } from "./intakeNav";
import { changes, filled, readSuggestions } from "./bodies/plaidSuggest";
import AnswerView from "./AnswerView";
import { BODIES } from "./bodies";
import {
  PART_KEYS,
  bodyOf,
  draftFrom,
  isAnswered,
  isCleared,
  lineErrorsOf,
} from "./intakeRules";
import {
  QP_CANCEL,
  QP_CLEARED,
  QP_EDIT,
  QP_EDIT_NAMED,
  QP_ITEM_TITLES,
  QP_SAVE,
  QP_SAVED,
} from "./intakeCopy";

// One item on the Questions page (spec § Questions page, DESIGN.md "Edit in
// place"): the answer or N/A; Edit swaps it for the page's own body in place;
// Save changes posts the page with `edit: true` and stays, rewriting the
// shown answer and saying "Saved" — or "Cleared" when the form was emptied
// (decision 16, R183); Cancel discards. Save is enabled when the form meets
// the page's answered rule or is empty. Staff see it read-only.
export default function QuestionItem({
  page,
  answer,
  vaultOk,
  onSaved,
  staff = false,
  plaid = null,
  startEditing = false,
  focusPart,
  Heading = "h2",
}) {
  const [draft, setDraft] = useState(() =>
    startEditing && vaultOk ? draftFrom(page, answer) : null,
  );
  const [lineErrors, setLineErrors] = useState({});
  const [result, setResult] = useState(null);
  const [notice, setNotice] = useState(null);
  const [sending, setSending] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const heading = useRef(null);
  const inFlight = useRef(false);
  const editBtn = useRef(null);
  const focusAfter = useRef(null);
  const title = QP_ITEM_TITLES[page.key];
  const headingId = `qp-${page.key}-h`;
  const editing = draft !== null;

  useEffect(() => {
    if (!startEditing) return;
    heading.current?.scrollIntoView();
    if (!PART_KEYS.includes(focusPart)) heading.current?.focus();
  }, [startEditing, focusPart]);
  // Focus follows the swap: the heading when the form opens (Edit unmounts),
  // the Edit button when it closes (the form unmounts).
  useEffect(() => {
    if (focusAfter.current === "heading" && editing) heading.current?.focus();
    if (focusAfter.current === "edit" && !editing) editBtn.current?.focus();
    focusAfter.current = null;
  }, [editing]);

  // R394: opening a Debts / Investments item for editing with accounts linked
  // reads Plaid's figures once and merges them into the draft (typed values
  // win); nothing is saved until Save changes, and Cancel discards them.
  const draftNow = useRef(draft);
  draftNow.current = draft;
  const suggest =
    editing && page.kind === "amounts" && plaid?.linked && plaid?.consent;
  useEffect(() => {
    if (!suggest) {
      setSuggested(null);
      return undefined;
    }
    let live = true;
    readSuggestions(page.key).then((res) => {
      if (!live) return;
      if (res.error) return setSuggested("error");
      if (!changes(draftNow.current, res.suggestions)) return;
      setDraft((d) => filled(d, res.suggestions));
      setSuggested("note");
    });
    return () => {
      live = false;
    };
  }, [suggest, page.key]);

  const cleared = editing && isCleared(page, draft);
  // Clearing is offered only on a page that has an answer to clear (the server
  // sends `null` for an unanswered page).
  const canSave =
    editing && (isAnswered(page, draft) || (cleared && answer != null));
  const open = () => {
    focusAfter.current = "heading";
    setResult(null);
    setNotice(null);
    setLineErrors({});
    setDraft(draftFrom(page, answer));
  };
  const save = async (e) => {
    e.preventDefault();
    if (!canSave || inFlight.current) return;
    const errors = cleared ? {} : lineErrorsOf(page, draft);
    setLineErrors(errors);
    if (Object.keys(errors).length) return;
    inFlight.current = true;
    setSending(true);
    setNotice(null);
    try {
      const body = bodyOf(page, draft);
      // R183: `edit: true` only on a clearing save.
      await saveIntakePage(page.key, cleared ? { ...body, edit: true } : body);
      focusAfter.current = "edit";
      onSaved(page.key, cleared ? null : body);
      setResult(cleared ? QP_CLEARED : QP_SAVED);
      setDraft(null);
    } catch (err) {
      setNotice(saveFailure(err));
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };
  const Body = BODIES[page.kind];

  return (
    <section
      aria-labelledby={headingId}
      className="tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6"
    >
      <Heading
        id={headingId}
        ref={heading}
        tabIndex={-1}
        className="tw-m-0 tw-flex tw-gap-3 tw-text-md tw-font-bold tw-text-text focus:tw-outline-none"
      >
        <span aria-hidden="true" className="tw-text-text-muted">
          {page.position}
        </span>
        {title}
      </Heading>
      {vaultOk && editing && (
        <form aria-label={QP_EDIT_NAMED(title)} noValidate onSubmit={save}>
          <Body
            page={page}
            draft={draft}
            onChange={setDraft}
            idPrefix={`qp-${page.key}`}
            focusPart={startEditing ? focusPart : undefined}
            lineErrors={lineErrors}
            suggested={suggested}
            Subheading="h3"
          />
          {notice && (
            <p
              role="alert"
              className="tw-mb-0 tw-mt-4 tw-text-sm tw-text-danger"
            >
              {notice}
            </p>
          )}
          <div className="tw-mt-6 tw-flex tw-justify-end tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-divider tw-pt-4">
            <Button
              variant="quiet"
              onClick={() => {
                focusAfter.current = "edit";
                setDraft(null);
              }}
            >
              {QP_CANCEL}
            </Button>
            <Button type="submit" disabled={!canSave} loading={sending}>
              {QP_SAVE}
            </Button>
          </div>
        </form>
      )}
      {vaultOk && !editing && (
        <div className="tw-mt-4 tw-grid tw-gap-4">
          <AnswerView page={page} answer={answer} />
          {!staff && (
            <div className="tw-flex tw-items-center tw-gap-3">
              <Button
                variant="quiet"
                ref={editBtn}
                aria-label={QP_EDIT_NAMED(title)}
                onClick={open}
              >
                {QP_EDIT}
              </Button>
            </div>
          )}
        </div>
      )}
      {!staff && (
        <span
          role="status"
          className="tw-mt-2 tw-block tw-text-sm tw-font-semibold tw-text-success"
        >
          {result}
        </span>
      )}
    </section>
  );
}
